import moment from "moment";
import { storage, database } from "../firebase";

export const retrieveClientDetails = async (info) => {
  try {
    if (info.companyId && info.clientId) {
      const client = await database
        .collection("companies")
        .doc(info.companyId)
        .collection("clients")
        .doc(info.clientId)
        .get();
      if (client.exists) {
        if (client.data().type && client.data().type === "company") {
          return {
            error: null,
            payload: {
              type: client.data().type ? client.data().type : null,
              logoUrl: client.data().logoUrl ? client.data().logoUrl : null,
              general: client.data().general ? client.data().general : null,
              address: client.data().address ? client.data().address : null,
              contact: client.data().contact ? client.data().contact : null,
              social: client.data().social ? client.data().social : null,
              financials: client.data().financials
                ? client.data().financials
                : null,
              timestamp: client.data().timestamp
                ? client.data().timestamp
                : null,
            },
          };
        } else if (client.data().type && client.data().type === "person") {
          return {
            error: null,
            payload: {
              type: client.data().type ? client.data().type : null,
              logoUrl: client.data().logoUrl ? client.data().logoUrl : null,
              general: client.data().general ? client.data().general : null,
              address: client.data().address ? client.data().address : null,
              contact: client.data().contact ? client.data().contact : null,
              social: client.data().social ? client.data().social : null,
              timestamp: client.data().timestamp
                ? client.data().timestamp
                : null,
            },
          };
        }
      } else {
        throw new Error("Kunden eksisterer ikke.");
      }
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveClientRoles = async (info) => {
  try {
    const roles = await database
      .collection("companies")
      .doc(info.companyId)
      .collection("clients")
      .doc(info.clientId)
      .get();
    if (roles.data().roles) {
      return { error: null, payload: roles.data().roles };
    } else {
      throw new Error("Ingen roller eksisterer.");
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveClientSales = async (info) => {
  try {
    if (info.companyId && info.year && info.clientId) {
      const allSales = [];

      const unfilteredStart = moment().set({ year: info.year }).startOf("year");
      const filterStart = unfilteredStart.unix();

      const unfilteredEnd = moment().set({ year: info.year }).endOf("year");
      const filteredEnd = unfilteredEnd.unix();

      const sales = await database
        .collection("companies")
        .doc(info.companyId)
        .collection("sales")
        .where("id", "==", info.clientId)
        .orderBy("timestamp")
        .startAt(filterStart)
        .endAt(filteredEnd)
        .get();
      if (sales.size > 0) {
        sales.forEach((sale) => {
          allSales.push({
            key: sale.id,
            id: sale.data().id,
            timestamp: sale.data().timestamp,
            description: sale.data().description,
            title: sale.data().title,
            price: sale.data().price,
            status: sale.data().status,
            vat: sale.data().vat,
          });
        });
      }
      return Promise.all(allSales).then((result) => {
        return { payload: result, error: null };
      });
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveClientNotes = async (info) => {
  try {
    const noteList = [];
    const allNotes = await database
      .collection("companies")
      .doc(info.companyId)
      .collection("clients")
      .doc(info.clientId)
      .collection("notes")
      .orderBy("timestamp")
      .get();
    if (allNotes.size > 0) {
      allNotes.forEach((note) => {
        const single = fetchNote(note.id, note.data());
        noteList.push(single);
      });
    }
    return Promise.all(noteList).then((result) => {
      return { payload: result, error: null };
    });
  } catch (e) {
    throw new Error(e);
  }
};

const fetchNote = (noteId, note) => {
  return new Promise((resolve, reject) => {
    database
      .collection("users")
      .doc(note.author)
      .get()
      .then((user) => {
        if (user.exists) {
          resolve({
            author: `${user.data().firstName} ${user.data().lastName}`,
            thumbnail: user.data().thumbnail,
            timestamp: note.timestamp,
            content: note.content,
            key: noteId,
          });
        } else {
          reject("error");
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const addNewSingleCompanySale = async (info) => {
  try {
    await database
      .collection("companies")
      .doc(info.companyId)
      .collection("sales")
      .add(info.sale);
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const updateSingleClientData = async (details) => {
  try {
    if (details.activeCompanyLogoUrl && details.activeCompanyLogo) {
      const url = await uploadClientLogo(details);
      await updateClientDetails(details, url, false);
      return { error: null };
    } else {
      if (
        details.activeCompanyLogo === undefined &&
        details.activeCompanyLogoUrl === undefined &&
        details.client.logoUrl
      ) {
        await deleteSingleCompanyLogo(details);
        await updateClientDetails(details, null, true);
        return { error: null };
      } else {
        await updateClientDetails(details, null, false);
        return { error: null };
      }
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const uploadClientLogo = async (info) => {
  try {
    var storageRef = storage.ref();
    var uploadRef = storageRef.child(
      `hosting/company/${info.companyId}/clients/${info.client.id}/thumbnail`
    );
    await uploadRef.put(info.activeCompanyLogo);
    const url = await fetchClientLogoDownloadUrl(info);
    return url;
  } catch (e) {
    throw new Error(e);
  }
};

export const updateClientDetails = async (details) => {
  try {
    if (details && details.client && details.client.type === "company") {
      await database
        .collection("companies")
        .doc(details.companyId)
        .collection("clients")
        .doc(details.clientId)
        .update({
          general: {
            employees:
              details.client &&
              details.client.general &&
              details.client.general.employees
                ? details.client.general.employees
                : null,
            industry:
              details.client &&
              details.client.general &&
              details.client.general.industry
                ? details.client.general.industry
                : null,
            parentCompany: {
              name:
                details.client &&
                details.client.general &&
                details.client.general.parentCompany &&
                details.client.general.parentCompany.name
                  ? details.client.general.parentCompany.name
                  : null,
              infoUrl:
                details.client &&
                details.client.general &&
                details.client.general.parentCompany &&
                details.client.general.parentCompany.infoUrl
                  ? details.client.general.parentCompany.infoUrl
                  : null,
              name:
                details.client &&
                details.client.general &&
                details.client.general.parentCompany &&
                details.client.general.parentCompany.name
                  ? details.client.general.parentCompany.name
                  : null,
              organizationNumber:
                details.client &&
                details.client.general &&
                details.client.general.parentCompany &&
                details.client.general.parentCompany.organizationNumber
                  ? details.client.general.parentCompany.organizationNumber
                  : null,
            },
            shareCapital:
              details.client &&
              details.client.general &&
              details.client.general.shareCapital
                ? details.client.general.shareCapital
                : null,
            shareholders:
              details.client &&
              details.client.general &&
              details.client.general.shareholders
                ? details.client.general.shareholders
                : null,
            organizationNumber:
              details.client &&
              details.client.general &&
              details.client.general.organizationNumber
                ? details.client.general.organizationNumber
                : null,
            established:
              details.client &&
              details.client.general &&
              details.client.general.established
                ? moment(details.client.general.established).unix()
                : null,
            roles:
              details.client &&
              details.client.general &&
              details.client.general.roles
                ? details.client.general.roles
                : null,
            name:
              details.client &&
              details.client.general &&
              details.client.general.name
                ? details.client.general.name
                : null,
          },
          address:
            details.client && details.client.address && details.client.address
              ? details.client.address
              : null,
          contact:
            details.client && details.client.contact && details.client.contact
              ? details.client.contact
              : null,
          social:
            details.client && details.client.social && details.client.social
              ? details.client.social
              : null,
          financials:
            details.client &&
            details.client.financials &&
            details.client.financials
              ? details.client.financials
              : null,
          type:
            details.client && details.client.type ? details.client.type : null,
        });
      return { error: null, payload: null };
    } else if (details && details.client && details.client.type === "person") {
      await database
        .collection("companies")
        .doc(details.companyId)
        .collection("clients")
        .doc(details.clientId)
        .update(details.client);
      return { error: null, payload: null };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const deleteSingleCompanyLogo = async (info) => {
  try {
    storage.refFromURL(info.client.logoUrl).delete();
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

const fetchClientLogoDownloadUrl = async (info) => {
  try {
    var storageRef = storage.ref();
    var downloadRef = storageRef.child(
      `hosting/company/${info.companyId}/clients/${info.client.id}/thumbnail`
    );
    return await downloadRef.getDownloadURL();
  } catch (e) {
    throw new Error(e);
  }
};

export const deleteSingleClient = async (info) => {
  try {
    const clientInfo = await database
      .collection("companies")
      .doc(info.companyId)
      .collection("clients")
      .doc(info.key)
      .get();
    if (clientInfo.data().logoUrl && clientInfo.data().logoUrl !== null) {
      await storage.refFromURL(clientInfo.data().logoUrl).delete();
      await database
        .collection("companies")
        .doc(info.companyId)
        .collection("clients")
        .doc(info.key)
        .delete();
      return { error: null };
    } else {
      await database
        .collection("companies")
        .doc(info.companyId)
        .collection("clients")
        .doc(info.key)
        .delete();
      return { error: null };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const deleteSingleClientSale = async (info) => {
  try {
    await database
      .collection("companies")
      .doc(info.companyId)
      .collection("sales")
      .doc(info.key)
      .delete();
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const updateSingleClientSale = async (info) => {
  try {
    await database
      .collection("companies")
      .doc(info.companyId)
      .collection("sales")
      .doc(info.sale.key)
      .update(info.sale);
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const addSingleClientNote = async (info) => {
  try {
    await database
      .collection("companies")
      .doc(info.companyId)
      .collection("clients")
      .doc(info.id)
      .collection("notes")
      .add(info.note);
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const deleteSingleClientNote = async (info) => {
  try {
    await database
      .collection("companies")
      .doc(info.companyId)
      .collection("clients")
      .doc(info.activeClient)
      .collection("notes")
      .doc(info.key)
      .delete();
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};
